import { useState, useEffect, useCallback, useRef } from 'react';

const HARVESTOR_API_URL = '/harvestor/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

function isLocalDevHost() {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

function resolveHarvestorFallbackUrl() {
  const configured = process.env.REACT_APP_HARVESTOR_URL;
  if (configured) {
    // Relative paths are already covered by /harvestor/api — no second fetch.
    if (configured.startsWith('/')) {
      return null;
    }
    // docker-compose defaults bake http://localhost:8081 into prod builds; skip off localhost.
    if ((configured.includes('localhost') || configured.includes('127.0.0.1')) && !isLocalDevHost()) {
      return null;
    }
    return `${configured.replace(/\/$/, '')}/api/feeds`;
  }

  if (isLocalDevHost()) {
    return 'http://localhost:8081/harvestor/api/feeds';
  }

  return null;
}

async function fetchFeedStatsFromApi() {
  const response = await fetch(`${API_BASE_URL}/feed/stats`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

async function fetchFeedsList() {
  try {
    const response = await fetch(`${HARVESTOR_API_URL}/feeds`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.feeds || [];
    }
  } catch {
    // Fall through to optional dev-only direct URL.
  }

  const directUrl = resolveHarvestorFallbackUrl();
  if (!directUrl) {
    throw new Error('Harvester unavailable');
  }

  const response = await fetch(directUrl, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data.feeds || [];
}

async function fetchRecent24h() {
  const response = await fetch(`${API_BASE_URL}/feed/recent/count?hours=24`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data.count ?? 0;
}

const initialState = {
  loading: false,
  error: null,
  feedStatsError: null,
  feedsError: null,
  recentError: null,
  totalArticles: null,
  activeSources: null,
  independentSources: null,
  recent24h: null,
  refreshedAt: null,
  feeds: null,
};

/**
 * Shared platform statistics from API feed stats, optional harvestor feed list,
 * and recent articles API.
 * @param {{ enabled?: boolean, includeFeeds?: boolean }} options
 *   - enabled: skip all fetches when false
 *   - includeFeeds: fetch per-feed list from harvestor (Stats page only)
 */
export function usePlatformStats({ enabled = true, includeFeeds = false } = {}) {
  const [state, setState] = useState(initialState);
  const fetchIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    let feeds = null;
    let feedsError = null;
    let feedStatsError = null;
    let totalArticles = null;
    let activeSources = null;
    let recent24h = null;
    let recentError = null;

    const tasks = [
      fetchFeedStatsFromApi()
        .then((stats) => {
          totalArticles = stats.totalArticles ?? null;
          activeSources = stats.activeSources ?? null;
        })
        .catch((err) => {
          feedStatsError = err.message;
        }),
      fetchRecent24h()
        .then((result) => {
          recent24h = result;
        })
        .catch((err) => {
          recentError = err.message;
        }),
    ];

    if (includeFeeds) {
      tasks.push(
        fetchFeedsList()
          .then((result) => {
            feeds = result;
          })
          .catch((err) => {
            feedsError = err.message;
          }),
      );
    }

    await Promise.all(tasks);

    if (fetchId !== fetchIdRef.current) {
      return;
    }

    if (totalArticles == null && feeds) {
      totalArticles = feeds.reduce((sum, feed) => sum + (feed.articleCount || 0), 0);
    }
    if (activeSources == null && feeds) {
      activeSources = feeds.filter((feed) => feed.enabled).length;
    }

    const totalFailure = Boolean(feedStatsError && recentError && totalArticles == null);
    const error = totalFailure ? 'Unable to load platform statistics' : null;

    setState({
      loading: false,
      error,
      feedStatsError,
      feedsError,
      recentError,
      totalArticles,
      activeSources,
      independentSources: activeSources,
      recent24h,
      refreshedAt: new Date().toISOString(),
      feeds,
    });
  }, [includeFeeds]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    refetch();
  }, [enabled, refetch]);

  return { ...state, refetch };
}

export default usePlatformStats;
